import { Form } from "@/components/ui/form";
import { Position } from "@/services/positionService";
import { ContactInfoFields } from "@/components/bid-request/ContactInfoFields";
import { EventLocationFields } from "@/components/bid-request/EventLocationFields";
import { EventDetailsFields } from "@/components/bid-request/EventDetailsFields";
import { AdditionalDetailsFields } from "@/components/bid-request/AdditionalDetailsFields";
import { BidFormValues } from "./types";
import { BidPositionsSection } from "./BidPositionsSection";
import DealerSpecialInstructions from "./DealerSpecialInstructions";
import DiscountAndFees from "./DiscountAndFees";
import { useEffect } from "react";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useQuery } from "@tanstack/react-query";
import { BidFormActions } from "./BidFormActions";
import { useBidForm } from "@/hooks/useBidForm";
import SplitPayment from "./SplitPayment";
import { ReferralCodeField } from "@/components/bid-request/ReferralCodeField";
import { useReferralCode } from "@/hooks/useReferralCode";
import { ContactsSection } from "./ContactsSection";
import { useBidContacts } from "@/hooks/useBidContacts";

interface BidDetailsFormProps {
  positions: Position[];
  eventId?: string;
  initialData?: BidFormValues;
}

export function BidDetailsForm({
  positions,
  eventId,
  initialData,
}: BidDetailsFormProps) {
  const {
    form,
    loading,
    handleSave,
    handlePartialSave,
    handleSendInvoice,
    handleSendBid,
    handleCreateEvent,
  } = useBidForm(eventId, initialData);

  const { contacts, setContacts, saveContacts } = useBidContacts(eventId);

  const {
    referralState,
    handleManualReferralChange,
    setAndValidateReferralCode,
    getSecureReferralCode,
  } = useReferralCode();

  const { data: bidData } = useQuery({
    queryKey: ["bid", eventId],
    queryFn: async () => {
      if (eventId === "new") return null;
      const response = await axiosInstance.get(`event/bid/${eventId}`);
      return {
        ...response.data,
        startTime: new Date(response.data.startTime),
        positions: response.data.positions || {},
      };
    },
    enabled: eventId !== "new",
    meta: {
      onSuccess: (data) => {
        if (data) {
          form.reset(data);
        }
      },
      onError: () => {
        console.error("Error fetching bid data");
      },
    },
  });

  useEffect(() => {
    if (bidData) {
      const formData = {
        ...bidData,
        wantUsToDoRaffle:
          bidData.wantUsToDoRaffle === null ? "NO" : bidData.wantUsToDoRaffle,
      };
      form.reset(formData);

      // Initialize referral code validation if present
      if (bidData.referralCode && bidData.referralCode.trim() !== "") {
        setAndValidateReferralCode(bidData.referralCode);
      }
    }
  }, [bidData, form, setAndValidateReferralCode]);

  const handleFormSubmit = async (values: BidFormValues) => {
    await handleSave(values);
  };

  // Custom handler to sync form field with referral validation
  const handleReferralChange = (value: string) => {
    form.setValue("referralCode", value);
    handleManualReferralChange(value);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-6"
      >
        <div className="space-y-6">
          <ContactInfoFields form={form} />
          <ContactsSection
            contacts={contacts}
            onChange={setContacts}
            disabled={loading}
          />
          <EventLocationFields form={form} />
          <EventDetailsFields form={form} />
          <AdditionalDetailsFields form={form} />
          <DealerSpecialInstructions form={form} />
          <DiscountAndFees form={form} />
          <BidPositionsSection positions={positions} eventId={eventId} />
          <SplitPayment form={form} />
          <ReferralCodeField
            control={form.control}
            name="referralCode"
            referralState={referralState}
            onReferralChange={handleReferralChange}
          />
        </div>

        <BidFormActions
          onSave={async () => {
            // A contact list that will not save should stop the whole save,
            // not be silently dropped while the bid goes through.
            if (!(await saveContacts())) {
              return;
            }
            await handlePartialSave();
          }}
          onSendInvoice={async () => {
            if (!(await saveContacts())) {
              return;
            }
            await handleSendInvoice();
          }}
          onSendBid={async () => {
            if (!(await saveContacts())) {
              return;
            }
            await handleSendBid();
          }}
          onCreateEvent={handleCreateEvent}
          loading={loading}
        />
      </form>
    </Form>
  );
}
